/** Time-zone helpers built on Intl (no tz database dependency). */
import type { EpochMs } from '@apos/shared';

/** Start new shoots in the device zone; users can choose the shoot zone explicitly. */
export function browserTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return 'UTC';
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(0);
    return zone;
  } catch {
    return 'UTC';
  }
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string): Intl.DateTimeFormat {
  let f = dtfCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dtfCache.set(tz, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function zonedParts(t: EpochMs, tz: string): ZonedParts {
  const parts: Record<string, string> = {};
  for (const p of dtf(tz).formatToParts(new Date(t))) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** Offset of `tz` from UTC at instant t, in ms (e.g. PDT → −7 h). */
export function tzOffsetMs(t: EpochMs, tz: string): number {
  const p = zonedParts(t, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(t / 1000) * 1000;
}

/** Wall-clock date "YYYY-MM-DD" + time "HH:MM" in `tz` → UTC epoch ms. */
export function zonedToUtc(date: string, time: string, tz: string): EpochMs {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const guess = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  let t = guess - tzOffsetMs(guess, tz);
  // second pass handles DST boundaries
  t = guess - tzOffsetMs(t, tz);
  return t;
}

/** UTC epoch → { date: 'YYYY-MM-DD', time: 'HH:MM' } in `tz`. */
export function utcToZoned(t: EpochMs, tz: string): { date: string; time: string } {
  const p = zonedParts(t, tz);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** Short zone abbreviation at t, e.g. "PDT". */
export function zoneAbbrev(t: EpochMs, tz: string): string {
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date(t));
    return p.find((x) => x.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

export const COMMON_TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'Pacific/Honolulu',
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Tokyo',
] as const;

/** Every IANA zone this browser knows (common ones first), plus `current` if it is not among them. */
export function timeZoneOptions(current?: string): { common: string[]; other: string[] } {
  let all: string[] = [];
  try {
    all = (Intl as unknown as { supportedValuesOf?(k: 'timeZone'): string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    /* older browsers: the common list only */
  }
  const common: string[] = [...COMMON_TIMEZONES];
  if (current && !common.includes(current) && !all.includes(current)) common.unshift(current);
  return { common, other: all.filter((z) => !common.includes(z)) };
}

/** "YYYY-MM-DD" + n days. */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n));
  return Number.isFinite(t.getTime()) ? t.toISOString().slice(0, 10) : date;
}

/** Whole days from date `a` to date `b` ("YYYY-MM-DD"). */
export function daysBetween(a: string, b: string): number {
  const ms = (s: string) => Date.parse(`${s}T00:00:00Z`);
  const n = Math.round((ms(b) - ms(a)) / 86400_000);
  return Number.isFinite(n) ? n : 0;
}

/**
 * A shoot window from wall-clock fields in `tz`. With no end date (or the same day) an end at or before
 * the start means it runs past midnight; with a later end date the window spans days (multi-day shoots).
 */
export function zonedWindow(date: string, start: string, endDate: string | null, end: string, tz: string): { start: EpochMs; end: EpochMs } {
  const s = zonedToUtc(date, start, tz);
  if (!endDate || endDate === date) {
    let e = zonedToUtc(date, end, tz);
    if (e <= s) e += 24 * 3600_000;
    return { start: s, end: e };
  }
  return { start: s, end: zonedToUtc(endDate, end, tz) };
}

/**
 * Rough check that `tz` is local to a longitude: the zone's UTC offset vs solar time (lon / 15 h).
 * True when they differ by more than 3 h — "this spot looks like it is in another time zone".
 */
export function zoneLooksWrongFor(lon: number, tz: string, at: EpochMs): boolean {
  try {
    const offH = tzOffsetMs(at, tz) / 3600_000;
    let d = Math.abs(offH - lon / 15) % 24;
    if (d > 12) d = 24 - d;
    return d > 3;
  } catch {
    return false;
  }
}
